import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { api } from '../../../api';
import {
  BasilBranch,
  BasilCanonSet,
  BasilFixture,
  BasilHttp,
  BasilVerifyClient,
  BranchesRead,
} from '../write/writeTypes';
import { readEpisode } from './readEpisode';

/**
 * The Basil read hook (spec sections 4.4 and 8.5).
 *
 * It renders the skeleton immediately from `preloadedNodes` — count and titles only — and then
 * performs its OWN full read, because `state.branch[]` carries no `steps` under the side-panel
 * flag. A read whose branches do not all carry a `steps` array fails by name and decompiles
 * nothing.
 *
 * A newer read abandons an older one: every request carries a monotonic id and a result whose id is
 * not the latest is dropped, so a fast re-read can never be overwritten by a slow first read.
 *
 * It also takes the STALENESS BASELINE (spec section 5.3): the verifier's own per-node canon lines
 * and digests for the read the script is decompiled from, asked for over the worker boundary. That
 * is where the baseline belongs — it must be the canon of the bytes the writer actually saw — and
 * it must come from the verifier's own artifact, never from a second implementation on the main
 * thread (ruling D2, gate check G8). Each read clears the previous canon first, so a baseline can
 * never outlive the read it belongs to. With no verifier there is no canon and the write lane's
 * guard refuses; the read itself still succeeds, because reading is not writing.
 */

export interface EpisodeScriptRead {
  /** the immutable raw read, kept for the verifier and the staleness baseline. */
  snapshot: BranchesRead | null;
  fixture: BasilFixture | null;
  /** the verifier's canon of `fixture`, taken with it. `null` until, or unless, the verifier answers. */
  canon: BasilCanonSet | null;
  requestState: 'idle' | 'loading' | 'success' | 'error';
  errorMessage: string | null;
  reread: () => Promise<void>;
  /** `preloadedNodes` count and titles: a paint-fast hint, never data. */
  skeletonTitles: string[];
}

export interface UseEpisodeScriptOptions {
  /** the modal is open; a closed modal reads nothing. */
  enabled: boolean;
  /** `this.state.branch` from the page — titles only, see section 4.4. */
  preloadedNodes?: BasilBranch[];
  /** the verifier seam; the canon of each read is asked for through it and nowhere else. */
  verifyClient?: BasilVerifyClient | null;
  /** the http seam; defaults to the page's axios instance. Tests pass a mock. */
  http?: BasilHttp;
}

export function useEpisodeScript(episodeId: number, options: UseEpisodeScriptOptions): EpisodeScriptRead {
  const {
    enabled, preloadedNodes, http, verifyClient,
  } = options;
  const [snapshot, setSnapshot] = useState<BranchesRead | null>(null);
  const [fixture, setFixture] = useState<BasilFixture | null>(null);
  const [canon, setCanon] = useState<BasilCanonSet | null>(null);
  const [requestState, setRequestState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  const httpRef = useRef<BasilHttp>(http || (api as unknown as BasilHttp));
  const verifyRef = useRef<BasilVerifyClient | null | undefined>(verifyClient);

  httpRef.current = http || (api as unknown as BasilHttp);
  verifyRef.current = verifyClient;

  // The signal the baseline effect runs on. It is a BOOLEAN, not the client's identity: a caller
  // that hands back a new client object on every render would otherwise ask the worker for the same
  // canon over and over. This flips exactly once, when the handshake answers.
  const verifierReady = Boolean(verifyClient && verifyClient.available);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    runIdRef.current += 1;

    const runId = runIdRef.current;

    setRequestState('loading');
    setErrorMessage(null);
    setCanon(null);

    const result = await readEpisode(httpRef.current, episodeId);

    if (!mountedRef.current || runId !== runIdRef.current) {
      return;
    }

    if (!result.ok) {
      setSnapshot(null);
      setFixture(null);
      setRequestState('error');
      setErrorMessage(result.message);

      return;
    }

    setSnapshot(result.snapshot);
    setFixture(result.fixture);
    setRequestState('success');
    setErrorMessage(null);
  }, [episodeId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    load();
  }, [enabled, load]);

  // The baseline. It runs on the fixture identity and on the verifier becoming ready, so it covers
  // both orders: a verifier that identified itself before the read, and one that answered after it.
  useEffect(() => {
    const client = verifyRef.current;

    if (!enabled || !verifierReady || !client || !fixture) {
      return undefined;
    }
    let live = true;
    const runId = runIdRef.current;

    client.canon(fixture)
      .then((baseline) => {
        if (live && mountedRef.current && runId === runIdRef.current) {
          setCanon(baseline);
        }
      })
      .catch(() => {
        if (live && mountedRef.current && runId === runIdRef.current) {
          setCanon(null);
        }
      });

    return () => {
      live = false;
    };
  }, [enabled, fixture, verifierReady]);

  const skeletonTitles = (preloadedNodes || []).map((node) => String(node.title || ''));

  return {
    snapshot,
    fixture,
    canon,
    requestState,
    errorMessage,
    reread: load,
    skeletonTitles,
  };
}
